import { useQuery } from "@apollo/client";
import { ExclamationIcon } from "@heroicons/react/solid";
import githubLogo from "@static/img/github.png";
import linearLogo from "@static/img/linear.png";
import salesforceLogo from "@static/img/salesforce.png";
import slackLogo from "@static/img/slack.png";
import teamsLogo from "@static/img/teams-64.png";
import youtrackLogo from "@static/img/youtrack.svg";
import { useEffect, useMemo, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";

import useLabel from "@apps/use-label/use-label";
import { assertNonNull } from "@helpers/helpers";

import {
  GetAvailableMergeIntegrationsQuery,
  GetMergeDevIntegrationsQuery,
  GetMergeDevIntegrationsQueryVariables,
  GetOrganizationIntegrationsQuery,
  GetOrganizationIntegrationsQueryVariables,
  GetSlackConfigurationQuery,
  GetSlackConfigurationQueryVariables,
  IntegrationTypes,
} from "../../../types/graphql-schema";
import getAvailableMergeIntegrationsQuery from "../graphql/get-available-merge-integrations";
import getMergeDevIntegrationsQuery from "../graphql/get-merge-dev-integrations";
import getOrganizationIntegrations from "../graphql/get-organization-integrations";
import getSlackConfiguration from "../graphql/get-slack-configuration";
import IntegrationDetailDialog from "./integrations/integration-detail-page";
import IntegrationsBrowser, {
  IntegrationCardData,
} from "./integrations/integrations-browser";
import {
  NATIVE_INTEGRATION_SLUGS,
  getNativeIntegrationsConfig,
} from "./integrations/integrations-config";
import {
  MERGE_CATEGORY_DESCRIPTIONS,
  isVisibleIntegrationCategory,
  normalizeIntegrationCategory,
} from "./integrations/integrations-constants";

const NATIVE_LOGOS: Record<string, string> = {
  slack: slackLogo,
  salesforce: salesforceLogo,
  teams: teamsLogo,
  github: githubLogo,
  linear: linearLogo,
  youtrack: youtrackLogo,
};

const Integrations = ({
  organization,
  canManage = true,
}: {
  organization: any;
  canManage?: boolean;
}) => {
  const label = useLabel();
  const mergeDevEnabled = waffle.flag_is_active("merge_dev_enabled");
  const location = useLocation();
  const history = useHistory();
  const [openSlug, setOpenSlug] = useState<string | null>(null);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const slug = params.get("open");
    if (slug) {
      if (canManage) {
        setOpenSlug(slug);
      }
      params.delete("open");
      const search = params.toString();
      history.replace({
        pathname: location.pathname,
        search: search ? `?${search}` : "",
      });
    }
  }, [canManage, location.pathname, location.search, history]);

  const { data: orgIntegrationsData } = useQuery<
    GetOrganizationIntegrationsQuery,
    GetOrganizationIntegrationsQueryVariables
  >(getOrganizationIntegrations, {
    variables: { organizationId: organization.id },
  });

  const { data: mergeIntegrationsData } = useQuery<
    GetMergeDevIntegrationsQuery,
    GetMergeDevIntegrationsQueryVariables
  >(getMergeDevIntegrationsQuery, {
    variables: { organizationId: organization.id },
  });

  const { data: availableMergeData } =
    useQuery<GetAvailableMergeIntegrationsQuery>(
      getAvailableMergeIntegrationsQuery,
      { skip: !mergeDevEnabled },
    );

  const { data: slackConfigData } = useQuery<
    GetSlackConfigurationQuery,
    GetSlackConfigurationQueryVariables
  >(getSlackConfiguration, {
    variables: { organizationId: organization.id },
  });

  // Prefer the dedicated Slack configuration query (authoritative + freshest)
  // and fall back to the organization fragment only while it is still loading,
  // so the card and the Slack modal always agree on connection state.
  const slackConnected = slackConfigData
    ? Boolean(slackConfigData.organization?.slackIndexingConfiguration)
    : Boolean(organization.hasSlackConnection);

  const connectedNativeSlugs = useMemo(() => {
    const set = new Set<string>();
    if (slackConnected) set.add("slack");
    if (
      organization.hasTeamsTranscriptConnection ||
      organization.hasTeamsInstallation
    ) {
      set.add("teams");
    }
    if (organization.hasGithubInstallation) set.add("github");
    if (organization.hasFellowTranscriptSyncConnection) set.add("fellow");
    if (organization.hasYoutrackIntegration) set.add("youtrack");
    const edges = orgIntegrationsData?.organization?.integrations.edges ?? [];
    edges.forEach((edge) => {
      const node = edge?.node;
      if (!node) return;
      if (node.integrationType === IntegrationTypes.Salesforce) {
        set.add("salesforce");
      } else if (node.integrationType === IntegrationTypes.Linear) {
        set.add("linear");
      }
    });
    return set;
  }, [organization, orgIntegrationsData, slackConnected]);

  const connectedMergeIntegrations = useMemo(
    () =>
      (mergeIntegrationsData?.mergeDevIntegrations ?? [])
        .filter((i): i is NonNullable<typeof i> => i !== null)
        .map((integration) =>
          assertNonNull({
            id: integration.id,
            name:
              integration.integrationDisplayName ||
              integration.integrationName ||
              "Unknown",
            slug: integration.integrationName || "",
            logo: integration.iconUrl || integration.fullImageUrl || "",
            category: integration.category,
          }),
        )
        .filter((integration) => integration.slug),
    [mergeIntegrationsData],
  );

  const enabledCards: IntegrationCardData[] = useMemo(() => {
    const config = getNativeIntegrationsConfig(organization, label);
    const native = Array.from(connectedNativeSlugs).map((slug) => ({
      slug,
      name: config[slug]?.name ?? slug,
      logo: config[slug]?.logo ?? NATIVE_LOGOS[slug] ?? "",
      summary: config[slug]?.summary ?? "",
      categories: config[slug]?.categories ?? [],
    }));
    const merge = connectedMergeIntegrations.map((integration) => ({
      slug: integration.slug,
      name: integration.name,
      logo: integration.logo,
      summary:
        MERGE_CATEGORY_DESCRIPTIONS[
          normalizeIntegrationCategory(integration.category)
        ] || `Connected ${integration.name}.`,
      categories: [integration.category],
    }));
    return [...native, ...merge];
  }, [connectedNativeSlugs, connectedMergeIntegrations, organization, label]);

  const availableCards: IntegrationCardData[] = useMemo(() => {
    const config = getNativeIntegrationsConfig(organization, label);
    const native = NATIVE_INTEGRATION_SLUGS.filter(
      (slug) => !connectedNativeSlugs.has(slug),
    ).map((slug) => ({
      slug,
      name: config[slug].name,
      logo: config[slug].logo,
      summary: config[slug].summary,
      categories: config[slug].categories,
    }));

    const connectedMergeSlugs = new Set(
      connectedMergeIntegrations.map((i) => i.slug),
    );
    const merge = mergeDevEnabled
      ? (availableMergeData?.availableMergeIntegrations ?? [])
          .filter((i): i is NonNullable<typeof i> => i !== null)
          .filter((integration) => !connectedMergeSlugs.has(integration.slug))
          .map((integration) => {
            const visibleCategories = integration.categories
              .filter((c): c is string => c !== null)
              .filter(isVisibleIntegrationCategory);
            return { integration, visibleCategories };
          })
          .filter(({ visibleCategories }) => visibleCategories.length > 0)
          .map(({ integration, visibleCategories }) => {
            const primary = visibleCategories[0];
            const summary =
              MERGE_CATEGORY_DESCRIPTIONS[
                normalizeIntegrationCategory(primary)
              ] || `Connect your ${integration.name} account.`;
            return {
              slug: integration.slug,
              name: integration.name,
              logo: integration.squareImage || integration.image || "",
              summary,
              categories: visibleCategories,
            };
          })
      : [];

    return [...native, ...merge];
  }, [
    organization,
    label,
    connectedNativeSlugs,
    connectedMergeIntegrations,
    mergeDevEnabled,
    availableMergeData,
  ]);

  const slackIndexingConfig = slackConfigData?.organization
    ?.slackIndexingConfiguration as
    | { hasBotPermissions?: boolean; hasBotContextPermissions?: boolean }
    | undefined;
  const needsBotContextReconnect =
    (slackIndexingConfig?.hasBotPermissions ?? false) &&
    !(slackIndexingConfig?.hasBotContextPermissions ?? false);

  return (
    <div>
      {needsBotContextReconnect && (
        <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 flex items-start gap-3">
          <ExclamationIcon className="size-5 text-amber-600 mt-0.5 shrink-0" />
          <div className="flex-1 space-y-1">
            <p className="text-sm font-semibold text-gray-900">
              Slack bot context is limited
            </p>
            <p className="text-sm text-gray-700">
              Reconnect Slack so the Topicflow bot can read prior channel
              messages when mentioned.{" "}
              <button
                type="button"
                onClick={() => setOpenSlug("slack")}
                className="font-medium text-blue-600 hover:underline"
              >
                Update permissions
              </button>
            </p>
          </div>
        </div>
      )}

      <IntegrationsBrowser
        enabled={enabledCards}
        available={availableCards}
        onSelect={canManage ? setOpenSlug : undefined}
        availableStatusLabel={canManage ? "Connect" : "Admin only"}
        availableStatusTone={canManage ? "neutral" : "warning"}
      />

      {canManage && (
        <IntegrationDetailDialog
          organization={organization}
          slug={openSlug ?? ""}
          open={!!openSlug}
          onClose={() => setOpenSlug(null)}
        />
      )}
    </div>
  );
};

export default Integrations;
